import { http } from '@/lib/apiClient';
import { tokenStorage } from '@/lib/tokenStorage';
import type {
  AuthUser,
  LoginRequest,
  LoginResponse,
  LoginResult,
  MfaSetupResponse,
  RegisterOperatoreInput,
  RegistrationAck,
} from '@/types';
import { isMfaChallenge } from '@/types';

/**
 * Auth endpoints. Token persistence for login/logout lives here; refresh-on-401
 * lives in `apiClient` so it can run inside the interceptor.
 */
export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResult> {
    const result = await http.post<LoginResult, LoginRequest>('/auth/login', credentials);
    if (!isMfaChallenge(result)) tokenStorage.set(result);
    return result;
  },

  mfaSetup(mfaToken: string): Promise<MfaSetupResponse> {
    return http.post<MfaSetupResponse, { mfaToken: string }>('/auth/mfa/setup', { mfaToken });
  },

  async mfaEnable(mfaToken: string, code: string): Promise<LoginResponse> {
    return http.post<LoginResponse, { mfaToken: string; code: string }>('/auth/mfa/enable', {
      mfaToken,
      code,
    });
  },

  async mfaVerify(mfaToken: string, code: string): Promise<LoginResponse> {
    return http.post<LoginResponse, { mfaToken: string; code: string }>('/auth/mfa/verify', {
      mfaToken,
      code,
    });
  },

  me(): Promise<AuthUser> {
    return http.get<AuthUser>('/auth/me');
  },

  getCurrentUser(): Promise<AuthUser> {
    return http.get<AuthUser>('/auth/me');
  },

  async logout(): Promise<void> {
    try {
      await http.post<void>('/auth/logout', {});
    } finally {
      tokenStorage.clear();
    }
  },

  requestPasswordReset(email: string): Promise<void> {
    return http.post<void>('/auth/forgot-password', { email });
  },

  resetPassword(token: string, password: string): Promise<void> {
    return http.post<void>('/auth/reset-password', { token, password });
  },

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return http.post<void>('/auth/change-password', { currentPassword, newPassword });
  },

  registerOperatore(input: RegisterOperatoreInput): Promise<RegistrationAck> {
    return http.post<RegistrationAck, RegisterOperatoreInput>('/auth/register-operatore', input);
  },

  verifyEmail(token: string): Promise<RegistrationAck> {
    return http.post<RegistrationAck, { token: string }>('/auth/verify-email', { token });
  },

  resendVerification(email: string, captchaToken?: string): Promise<RegistrationAck> {
    return http.post<RegistrationAck, { email: string; captchaToken?: string }>('/auth/resend-verification', {
      email,
      captchaToken,
    });
  },
};
